import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, ResponsiveContainer, Label
} from 'recharts';
import {
  FaSpinner, FaMoneyBillWave, FaHandHoldingUsd, FaCoins, FaReceipt,
  FaUsers, FaHandshake, FaDonate, FaUserFriends, FaChartLine,
  FaChartPie, FaWallet, FaArrowUp, FaCalendarAlt, FaIndustry,
  FaTachometerAlt, FaChargingStation, FaPercent, FaDollarSign,
  FaEnvelopeOpenText, FaPlusCircle, FaChartBar, FaUserPlus,
  FaBuilding, FaFileInvoiceDollar, FaFlagCheckered
} from 'react-icons/fa';
import { useAuthContext } from '../../context/AuthContext';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import { eventApi } from '../../api/events';
import { budgetApi } from '../../api/budget';
import './Takwimu.css';
import '../ecards/admin-ecards.css';

const Takwimu = () => {
  const { currentEvent, isAuthenticated } = useAuthContext();
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    totalBudget: 0,
    totalPledges: 0,
    totalCashCollected: 0,
    totalExpenditures: 0,
    totalGuests: 0,
    walioAhidi: 0,
    walioChangia: 0,
    waalikwa: 0,
    budgetChange: '+0%',
    pieData: []
  });

  useEffect(() => {
    if (isAuthenticated && currentEvent) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, currentEvent]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let eventStats = {};
      try {
        const statsResponse = await eventApi.getEventStats();
        eventStats = statsResponse?.data?.data || statsResponse?.data || {};
      } catch (err) {
        console.log('Event stats not available yet:', err.message);
      }

      let generatedPieData = [];
      try {
        const budgetRes = await budgetApi.getBudgetItems();
        let items = [];
        if (budgetRes?.data?.data && Array.isArray(budgetRes.data.data)) {
          items = budgetRes.data.data;
        } else if (Array.isArray(budgetRes?.data)) {
          items = budgetRes.data;
        } else if (Array.isArray(budgetRes)) {
          items = budgetRes;
        }

        // Define colors that match the 3D pie chart style
        const colors = ['#1e293b', '#6b21a8', '#dc2626', '#d97706', '#94a3b8', '#0ea5e9', '#10b981', '#ec4899'];

        generatedPieData = items.map((item, index) => ({
          name: item.item || item.category_name || `Kipengele ${index + 1}`,
          value: parseFloat(item.amount) || 0,
          color: colors[index % colors.length]
        })).filter(i => i.value > 0);

      } catch (err) {
        console.log('Could not fetch specific budget items:', err.message);
      }

      if (generatedPieData.length === 0) {
        generatedPieData = [
          { name: 'Bajeti Kamili', value: parseFloat(eventStats.budget) || 0, color: '#1e293b' }
        ].filter(item => item.value > 0);
      }

      const budgetVal = parseFloat(eventStats.budget) || 0;
      const contributionsVal = parseFloat(eventStats.contributions) || 0;
      const pledgesVal = parseFloat(eventStats.pledges) || 0;
      const expensesVal = parseFloat(eventStats.expenses) || 0;

      setStats({
        totalBudget: budgetVal,
        totalPledges: pledgesVal + contributionsVal, // Total pledges = Pledges + Cash Contributions
        totalCashCollected: contributionsVal,
        totalExpenditures: expensesVal,
        totalGuests: parseInt(eventStats.guests) || 0,
        walioAhidi: parseInt(eventStats.pledgers) || 0,
        walioChangia: parseInt(eventStats.contributors) || 0,
        waalikwa: parseInt(eventStats.invited) || 0,
        walioHudhria: parseInt(eventStats.attendance) || parseInt(eventStats.attendance_count) || 0,
        budgetChange: '+0%',
        pieData: generatedPieData
      });
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderCustomizedLabel = (props) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent, index, value, name, fill } = props;
    const RADIAN = Math.PI / 180;
    
    // Stagger radius based on index to prevent label overlap
    const isEven = index % 2 === 0;
    const radiusFactor = isEven ? 1.3 : 2.0;
    const radius = innerRadius + (outerRadius - innerRadius) * radiusFactor;
    
    let x = cx + radius * Math.cos(-midAngle * RADIAN);
    let y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Give an extra vertical nudge to separate them further
    y = y + (isEven ? -12 : 12);

    const textAnchor = x > cx ? 'start' : 'end';
    const boxWidth = 125;
    const boxHeight = 28;
    const rectX = textAnchor === 'start' ? x : x - boxWidth;
    const rectY = y - boxHeight / 2;

    const lineStartX = cx + outerRadius * Math.cos(-midAngle * RADIAN);
    const lineStartY = cy + outerRadius * Math.sin(-midAngle * RADIAN);
    const breakX = textAnchor === 'start' ? x - 10 : x + 10;

    return (
      <g>
        <path
          d={`M${lineStartX},${lineStartY} L${breakX},${y} L${textAnchor === 'start' ? x - 2 : x + 2},${y}`}
          stroke={fill}
          fill="none"
          strokeWidth={1.5}
        />
        {/* Pill shaped label */}
        <rect
          x={rectX} y={rectY - 10}
          width={boxWidth} height={boxHeight}
          rx={14} ry={14}
          fill={fill}
          style={{ filter: 'drop-shadow(0 3px 5px rgba(0,0,0,0.25))' }}
        />
        <text x={rectX + boxWidth / 2} y={rectY + 8} textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold">
          {name?.length > 15 ? name.substring(0, 15) + '...' : name}
        </text>
        <text x={rectX + boxWidth / 2} y={rectY + 30} textAnchor="middle" fill="#64748b" fontSize="10" fontWeight="600">
          {(percent * 100).toFixed(1)}% | TZS {value >= 1000000 ? (value / 1000000).toFixed(1) + 'M' : (value >= 1000 ? (value / 1000).toFixed(0) + 'K' : value)}
        </text>
      </g>
    );
  };

  const pct = (value, total) => {
    if (total === 0) return 0;
    return (value / total) * 100;
  };

  const financialChartData = [
    { name: 'Bajeti', amount: stats.totalBudget, fill: '#3b82f6' },
    { name: 'Ahadi', amount: stats.totalPledges, fill: '#10b981' },
    { name: 'Pesa Taslimu', amount: stats.totalCashCollected, fill: '#f59e0b' },
    { name: 'Matumizi', amount: stats.totalExpenditures, fill: '#ef4444' },
  ];

  const CustomBarShape = (props) => {
    const { fill, x, y, width, height, payload } = props;
    const centerX = x + width / 2;
    const labelY = y - 10; // Place labels slightly above the top of the bar shape
    
    return (
      <g>
        {/* Value Label above the Bar */}
        <text 
          x={centerX} 
          y={labelY} 
          fill={fill} 
          fontSize={11} 
          fontWeight="bold" 
          textAnchor="middle"
        >
          {payload.amount >= 1000000 
            ? (payload.amount / 1000000).toFixed(2) + 'M' 
            : payload.amount.toLocaleString()}
        </text>

        {/* Flat colored bar */}
        <rect x={x} y={y} width={width} height={height} fill={fill} rx={4} ry={4} />
      </g>
    );
  };

  const CustomXAxisTick = ({ x, y, payload }) => {
    const item = financialChartData.find(d => d.name === payload.value);
    const fill = item ? item.fill : '#64748b';
    return (
      <text x={x} y={y + 15} textAnchor="middle" fill={fill} fontSize={12} fontWeight="bold">
        {payload.value}
      </text>
    );
  };

  if (loading) {
    return (
      <div className="takwimu-loading">
        <div className="loading-content">
          <div className="loading-spinner-modern">
            <FaSpinner className="fa-spin" size={48} color="#3b82f6" />
          </div>
          <p className="loading-text">Loading statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="takwimu-dashboard">
      <div className="dashboard">
        {/* Header */}
        <div className="col-12 mt-4 mb-4">
          <br />
          <h1 className="stylish-heading text-center" style={{ color: '#4169e1' }}>Takwimu · Fedha & Wageni</h1>
          <p className="text-muted text-center">Uchambuzi wa kina: Bajeti, Ahadi, Pesa Taslimu, Matumizi</p>
        </div>

        {/* ROW: Two Cards - Finance & Guests */}
        <div className="cards-row">
          {/* CARD 1: FEDHA / FINANCE - Modern Premium Design */}
          <div className="modern-metric-card">
            <div className="card-header-premium d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3">
                <div className="header-icon-wrapper bg-blue-light">
                  <FaCoins className="text-blue" />
                </div>
                <div className="header-title-wrapper">
                  <h3>Fedha | Finance </h3>
                  <p>Uchambuzi wa mapato na matumizi</p>
                </div>
              </div>
              <div className="header-total text-end">
                <div className="fw-bold fs-4 text-dark">TZS {stats.totalBudget.toLocaleString()}</div>
                <div className="text-muted" style={{ fontSize: '0.8rem', fontWeight: 600 }}>Bajeti Jumla</div>
              </div>
            </div>

            <div className="stats-list">

              {/* Ahadi */}
              <div className="stat-item-modern">
                <div className="stat-icon-modern bg-green-light">
                  <FaHandHoldingUsd className="text-green" />
                </div>
                <div className="stat-details-modern">
                  <div className="stat-title-row">
                    <span className="stat-label">Ahadi Zote</span>
                    <span className="stat-amount text-green">TZS {stats.totalPledges.toLocaleString()}</span>
                  </div>
                  <div className="progress-modern-container">
                    <div className="progress-modern-fill bg-green" style={{ width: `${Math.min(pct(stats.totalPledges, stats.totalBudget), 100)}%` }}></div>
                  </div>
                  <div className="stat-subtext d-flex justify-content-between">
                    <span>{pct(stats.totalPledges, stats.totalBudget).toFixed(0)}% ya Bajeti</span>
                    <span>Ahadi Kamili</span>
                  </div>
                </div>
              </div>

              {/* Pesa Taslimu */}
              <div className="stat-item-modern">
                <div className="stat-icon-modern bg-orange-light">
                  <FaMoneyBillWave className="text-orange" />
                </div>
                <div className="stat-details-modern">
                  <div className="stat-title-row">
                    <span className="stat-label">Pesa Taslimu</span>
                    <span className="stat-amount text-orange">TZS {stats.totalCashCollected.toLocaleString()}</span>
                  </div>
                  <div className="progress-modern-container">
                    <div className="progress-modern-fill bg-orange" style={{ width: `${Math.min(pct(stats.totalCashCollected, stats.totalBudget), 100)}%` }}></div>
                  </div>
                  <div className="stat-subtext d-flex justify-content-between">
                    <span>{pct(stats.totalCashCollected, stats.totalBudget).toFixed(1)}% ya Bajeti</span>
                    <span>{pct(stats.totalCashCollected, stats.totalPledges).toFixed(1)}% ya Ahadi</span>
                  </div>
                </div>
              </div>

              {/* Matumizi */}
              <div className="stat-item-modern border-bottom-0 pb-0">
                <div className="stat-icon-modern bg-red-light">
                  <FaReceipt className="text-red" />
                </div>
                <div className="stat-details-modern">
                  <div className="stat-title-row">
                    <span className="stat-label">Matumizi</span>
                    <span className="stat-amount text-red">TZS {stats.totalExpenditures.toLocaleString()}</span>
                  </div>
                  <div className="progress-modern-container">
                    <div className="progress-modern-fill bg-red" style={{ width: `${Math.min(pct(stats.totalExpenditures, stats.totalBudget), 100)}%` }}></div>
                  </div>
                  <div className="stat-subtext d-flex justify-content-between">
                    <span>{pct(stats.totalExpenditures, stats.totalBudget).toFixed(1)}% ya Bajeti</span>
                    <span>{pct(stats.totalExpenditures, stats.totalCashCollected).toFixed(1)}% ya Taslimu</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="premium-footer mt-4">
              <div className="d-flex align-items-center gap-2">
                <FaChartLine className="text-blue" />
                <span className="footer-label">Maendeleo ya Bajeti</span>
              </div>
              <span className="footer-trend text-green">{stats.totalBudget > 0 ? pct(stats.totalCashCollected, stats.totalBudget).toFixed(1) : 0}% <small>ya lengo imekusanywa</small></span>
            </div>
          </div>

          {/* CARD 2: WAGENI - Modern Premium Design */}
          <div className="modern-metric-card">
            <div className="card-header-premium d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3">
                <div className="header-icon-wrapper bg-purple-light">
                  <FaUsers className="text-purple" />
                </div>
                <div className="header-title-wrapper">
                  <h3>Wageni </h3>
                  <p>Uchambuzi wa wageni na waalikwa</p>
                </div>
              </div>
              <div className="header-total text-end">
                <div className="fw-bold fs-4 text-dark">{stats.totalGuests}</div>
                <div className="text-muted" style={{ fontSize: '0.8rem', fontWeight: 600 }}>Jumla ya Wageni</div>
              </div>
            </div>

            <div className="stats-list">

              {/* Walioahidi */}
              <div className="stat-item-modern">
                <div className="stat-icon-modern bg-green-light">
                  <FaHandshake className="text-green" />
                </div>
                <div className="stat-details-modern">
                  <div className="stat-title-row">
                    <span className="stat-label">Walioahidi</span>
                    <span className="stat-amount text-green">{stats.walioAhidi}</span>
                  </div>
                  <div className="progress-modern-container">
                    <div className="progress-modern-fill bg-green" style={{ width: `${Math.min(pct(stats.walioAhidi, stats.totalGuests), 100)}%` }}></div>
                  </div>
                  <div className="stat-subtext d-flex justify-content-between">
                    <span>{pct(stats.walioAhidi, stats.totalGuests).toFixed(0)}% ya Wageni</span>
                    <span>Waliotoa ahadi</span>
                  </div>
                </div>
              </div>

              {/* Waliochangia */}
              <div className="stat-item-modern">
                <div className="stat-icon-modern bg-orange-light">
                  <FaDonate className="text-orange" />
                </div>
                <div className="stat-details-modern">
                  <div className="stat-title-row">
                    <span className="stat-label">Waliochangia</span>
                    <span className="stat-amount text-orange">{stats.walioChangia}</span>
                  </div>
                  <div className="progress-modern-container">
                    <div className="progress-modern-fill bg-orange" style={{ width: `${Math.min(pct(stats.walioChangia, stats.totalGuests), 100)}%` }}></div>
                  </div>
                  <div className="stat-subtext d-flex justify-content-between">
                    <span>{pct(stats.walioChangia, stats.totalGuests).toFixed(1)}% ya Wageni</span>
                    <span>{pct(stats.walioChangia, stats.walioAhidi).toFixed(1)}% ya walioahidi</span>
                  </div>
                </div>
              </div>

              {/* Waalikwa */}
              <div className="stat-item-modern border-bottom-0 pb-0">
                <div className="stat-icon-modern bg-blue-light">
                  <FaEnvelopeOpenText className="text-blue" />
                </div>
                <div className="stat-details-modern">
                  <div className="stat-title-row">
                    <span className="stat-label">Waalikwa</span>
                    <span className="stat-amount text-blue">{stats.waalikwa}</span>
                  </div>
                  <div className="progress-modern-container">
                    <div className="progress-modern-fill bg-blue" style={{ width: `${Math.min(pct(stats.waalikwa, stats.totalGuests), 100)}%` }}></div>
                  </div>
                  <div className="stat-subtext d-flex justify-content-between">
                    <span>{pct(stats.waalikwa, stats.totalGuests).toFixed(0)}% ya Wageni</span>
                    <span>Waliopata mualiko</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="premium-footer mt-4">
              <div className="d-flex align-items-center gap-2">
                <FaUserPlus className="text-purple" />
                <span className="footer-label">Wageni Waliothibitisha</span>
              </div>
              <span className="footer-trend text-green">{stats.totalGuests > 0 ? pct(stats.walioAhidi, stats.totalGuests).toFixed(1) : 0}% <small>ya wageni wote</small></span>
            </div>
          </div>
        </div>

        {/* GRAPH SECTION */}
        <div className="charts-container">
          <div className="chart-card">
            <div className="chart-title">
              <FaChartBar /> Muhtasari wa Fedha · Grafu
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={financialChartData} margin={{ top: 50, right: 30, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={<CustomXAxisTick />} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                <Tooltip cursor={{ fill: 'transparent' }} formatter={(value) => [`TZS ${value.toLocaleString()}`, '']} />
                <Bar dataKey="amount" shape={<CustomBarShape />} barSize={55} />
              </BarChart>
            </ResponsiveContainer>
            <div className="chart-footer">
              Bajeti, Ahadi, Pesa Taslimu, Matumizi (TZS)
            </div>
          </div>
          <div className="chart-card">
            <div className="chart-title text-center mb-4">
              <FaChartPie /> Mgawanyo wa Bajeti (3D View)
            </div>

            <div className="d-flex flex-column align-items-center mt-3">
              <div style={{ width: '100%', minHeight: '400px' }}>
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart margin={{ top: 30, right: 30, bottom: 30, left: 30 }}>
                    <defs>
                      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="2" dy="6" stdDeviation="4" floodOpacity="0.4" />
                      </filter>
                    </defs>
                    <Pie
                      data={stats.pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={95}
                      paddingAngle={5}
                      dataKey="value"
                      nameKey="name"
                      stroke="none"
                      labelLine={false}
                      label={renderCustomizedLabel}
                    >
                      {stats.pieData.map((entry, i) => (
                        <Cell key={`cell-${i}`} fill={entry.color} style={{ filter: 'url(#shadow)' }} />
                      ))}
                      <Label
                        value={stats.totalBudget >= 1000000 ? `TZS ${(stats.totalBudget / 1000000).toFixed(1)}M` : `TZS ${(stats.totalBudget / 1000).toFixed(0)}K`}
                        position="center"
                        fill="#1e293b"
                        style={{ fontSize: '1rem', fontWeight: 800 }}
                      />
                      <Label
                        value="Jumla"
                        position="center"
                        dy={18}
                        fill="#64748b"
                        style={{ fontSize: '0.75rem', fontWeight: 600 }}
                      />
                    </Pie>
                    <Tooltip formatter={(value) => [`TZS ${value.toLocaleString()}`, 'Bajeti']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="chart-footer mt-3">
              Sehemu za matumizi kulingana na vipaumbele
            </div>
          </div>
        </div>

        {/* ADDITIONAL MINI-CARDS */}
        <div className="insight-section">
          <div className="insight-header">
            Takwimu za Kina za Ushiriki
          </div>
          <div className="extra-stats-row">
            <div className="insight-card">
              <FaUserFriends className="insight-icon" />
              <div className="insight-number">{stats.totalGuests}</div>
              <div className="insight-label">Wageni Wote</div>
              <div className="insight-sub">Kwenye orodha</div>
            </div>
            <div className="insight-card">
              <FaEnvelopeOpenText className="insight-icon" style={{ color: '#3b82f6' }} />
              <div className="insight-number">{stats.waalikwa}</div>
              <div className="insight-label">Waalikwa</div>
              <div className="insight-sub">{stats.totalGuests > 0 ? pct(stats.waalikwa, stats.totalGuests).toFixed(0) : 0}% ya wageni</div>
            </div>
            <div className="insight-card">
              <FaFlagCheckered className="insight-icon" style={{ color: '#10b981' }} />
              <div className="insight-number">{stats.walioHudhria}</div>
              <div className="insight-label">Waliohudhuria</div>
              <div className="insight-sub">{stats.waalikwa > 0 ? pct(stats.walioHudhria, stats.waalikwa).toFixed(0) : 0}% ya waalikwa</div>
            </div>
            <div className="insight-card">
              <FaWallet className="insight-icon" />
              <div className="insight-number">
                TZS {stats.totalCashCollected >= 1000000 ? (stats.totalCashCollected / 1000000).toFixed(2) + 'M' : (stats.totalCashCollected >= 1000 ? (stats.totalCashCollected / 1000).toFixed(0) + 'K' : stats.totalCashCollected)}
              </div>
              <div className="insight-label">Pesa Taslimu</div>
              <div className="insight-sub">{stats.totalBudget > 0 ? pct(stats.totalCashCollected, stats.totalBudget).toFixed(0) : 0}% ya Bajeti</div>
            </div>
          </div>
        </div>

        {/* Additional Reference Data Section */}



      </div>
    </div>
  );
};

export default Takwimu;